import { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  UserPlus, Trash, Copy, Crown, User, ShieldStar, EnvelopeSimple,
  ShieldWarning, PaperPlaneTilt,
} from "@phosphor-icons/react";

const RoleBadge = ({ role }) => {
  const map = {
    owner: { icon: Crown, cls: "bg-orange-50 text-[#C2410C] border-orange-100" },
    admin: { icon: ShieldStar, cls: "bg-indigo-50 text-[#4338CA] border-indigo-100" },
    member: { icon: User, cls: "bg-stone-100 text-stone-600 border-stone-200" },
  };
  const { icon: Icon, cls } = map[role] || map.member;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${cls}`}>
      <Icon size={12} weight="fill" /> {role}
    </span>
  );
};

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState({ members: [], invitations: [] });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const isManager = user?.role === "owner" || user?.role === "admin";
  const isOwner = user?.role === "owner";

  const load = () => api.get("/team/members").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (isManager) load(); }, [isManager]);

  const invite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: res } = await api.post("/team/invite", { email: email.trim(), name: name.trim() || null, role });
      setLastInvite(res);
      toast.success(res.email_sent ? "Invitation email sent" : "Invitation created — copy the link to share");
      setEmail(""); setName(""); setRole("member");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resend = async (i) => {
    try {
      const { data: res } = await api.post(`/team/invitations/${i.id}/resend`);
      setLastInvite(res);
      toast.success(res.email_sent ? "Invitation re-sent" : "Link refreshed — copy it to share");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.email} from the workspace?`)) return;
    try { await api.delete(`/team/members/${m.id}`); toast.success("Member removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const changeRole = async (m, newRole) => {
    try { await api.patch(`/team/members/${m.id}/role`, { role: newRole }); toast.success(`${m.email} is now ${newRole}`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const revoke = async (i) => {
    try { await api.delete(`/team/invitations/${i.id}`); toast.success("Invitation revoked"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Invite link copied"); };

  const canRemove = (m) => !m.is_you && m.role !== "owner" && (m.role !== "admin" || isOwner);

  if (!isManager) {
    return (
      <Layout>
        <PageHeader title="Team" subtitle="Manage workspace members" />
        <div className="p-8">
          <div data-testid="team-forbidden" className="max-w-md flex gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-stone-700">
            <ShieldWarning size={22} weight="fill" className="text-amber-600 shrink-0" />
            <span>Only workspace <b>owners</b> and <b>admins</b> can manage members. Ask an owner or admin to invite you or change your role.</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader title="Team" subtitle="Invite and manage members of your workspace" />
      <div className="p-8 max-w-4xl space-y-8">
        {/* Invite form */}
        <div className="bg-white border border-stone-200 rounded-2xl shadow-card p-6">
          <h3 className="font-head font-semibold text-lg text-stone-900 flex items-center gap-2">
            <UserPlus size={20} weight="duotone" className="text-[#C2410C]" /> Invite a member
          </h3>
          <p className="text-sm text-stone-500 mt-1 mb-5">They'll get a secure link to set a password and join your workspace.</p>
          <form onSubmit={invite} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs uppercase tracking-wider text-stone-500">Email</Label>
              <Input data-testid="invite-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-xl" placeholder="colleague@company.com" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <Label className="text-xs uppercase tracking-wider text-stone-500">Name (optional)</Label>
              <Input data-testid="invite-name-input" value={name}
                onChange={(e) => setName(e.target.value)} className="mt-1 rounded-xl" placeholder="Jane Doe" />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs uppercase tracking-wider text-stone-500">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select" className="mt-1 rounded-xl h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  {isOwner && <SelectItem value="admin">Admin</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="send-invite-btn" type="submit" disabled={loading}
              className="rounded-full bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 gap-2">
              <UserPlus size={16} /> {loading ? "Sending…" : "Send invite"}
            </Button>
          </form>
          {!isOwner && (
            <p className="text-xs text-stone-400 mt-3">As an admin you can invite members. Only the owner can invite other admins.</p>
          )}
          {lastInvite && (
            <div data-testid="invite-link-box" className="mt-4 flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl p-3">
              <span className="text-xs text-stone-500 shrink-0">{lastInvite.email_sent ? "Also shareable:" : "Share this link:"}</span>
              <code className="text-xs text-stone-700 truncate flex-1">{lastInvite.invite_url}</code>
              <Button data-testid="copy-invite-btn" size="sm" variant="outline" onClick={() => copy(lastInvite.invite_url)} className="gap-1.5 text-xs shrink-0">
                <Copy size={13} /> Copy
              </Button>
            </div>
          )}
        </div>

        {/* Members */}
        <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-100">
            <h3 className="font-head font-semibold text-stone-900">Members <span className="text-stone-400">({data.members.length})</span></h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-100">
                <th className="px-6 py-3">Name</th><th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Role</th><th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="members-table">
              {data.members.map((m) => (
                <tr key={m.id} className="border-b border-stone-50 last:border-0">
                  <td className="px-6 py-3 font-medium text-stone-900">{m.name || "—"} {m.is_you && <span className="text-xs text-stone-400">(you)</span>}</td>
                  <td className="px-6 py-3 text-stone-600">{m.email}</td>
                  <td className="px-6 py-3">
                    {isOwner && !m.is_you && m.role !== "owner" ? (
                      <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                        <SelectTrigger data-testid={`role-select-${m.id}`} className="h-8 w-32 rounded-xl text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : <RoleBadge role={m.role} />}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {canRemove(m) && (
                      <Button data-testid={`remove-member-${m.id}`} size="sm" variant="ghost"
                        onClick={() => removeMember(m)} className="text-rose-600 hover:bg-rose-50"><Trash size={15} /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pending invitations */}
        {data.invitations.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100">
              <h3 className="font-head font-semibold text-stone-900 flex items-center gap-2">
                <EnvelopeSimple size={18} className="text-stone-400" /> Pending invitations <span className="text-stone-400">({data.invitations.length})</span>
              </h3>
            </div>
            <table className="w-full text-sm">
              <tbody data-testid="invites-table">
                {data.invitations.map((i) => (
                  <tr key={i.id} className="border-b border-stone-50 last:border-0">
                    <td className="px-6 py-3 text-stone-700">{i.email}</td>
                    <td className="px-6 py-3"><RoleBadge role={i.role} /></td>
                    <td className="px-6 py-3">
                      <span className={`text-xs rounded-full px-2.5 py-0.5 ${i.expired ? "bg-rose-50 text-rose-600 border border-rose-100" : "bg-amber-50 text-amber-700 border border-amber-100"}`}>
                        {i.expired ? "expired" : "pending"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      <Button data-testid={`resend-invite-${i.id}`} size="sm" variant="ghost"
                        onClick={() => resend(i)} className="text-[#C2410C] hover:bg-orange-50 gap-1.5"><PaperPlaneTilt size={14} /> Resend</Button>
                      <Button data-testid={`revoke-invite-${i.id}`} size="sm" variant="ghost"
                        onClick={() => revoke(i)} className="text-rose-600 hover:bg-rose-50"><Trash size={15} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
